// Token management utilities

const TOKEN_KEY = 'oshmAuthToken';
const USER_KEY = 'oshmUser';

const SESSION_KEYS_TO_CLEAR_ON_AUTH_CHANGE = [
  'pendingAssessment',
  'oshmUserDetails',
  'ba_currentQuestion',
  'ba_responses',
  'demo_ba_currentQuestion',
  'demo_ba_responses',
  'ba_paid_structural_system',
  'bridge_currentQuestion',
  'bridge_responses',
  // One draft per bridge type. Without these, a user who logs out leaves their half-filled bridge
  // assessment behind for the next person to log in on the same browser tab.
  'rcc_bridge_currentQuestion',
  'rcc_bridge_responses',
  'psc_bridge_currentQuestion',
  'psc_bridge_responses',
  'steel_bridge_currentQuestion',
  'steel_bridge_responses',
  'composite_bridge_currentQuestion',
  'composite_bridge_responses',
  'masonry_bridge_currentQuestion',
  'masonry_bridge_responses',
  'tunnel_currentQuestion',
  'tunnel_responses',
  'assessmentResponses',
  'buildingAssessmentReport',
  'tunnelAssessmentReport',
  'serverPdfBlob',
  'advancedGuideReturnTo',
  'oshmSubmitterDetails'
];

const clearAssessmentSessionState = () => {
  SESSION_KEYS_TO_CLEAR_ON_AUTH_CHANGE.forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

/**
 * Store authentication token in sessionStorage (clears when browser closes)
 */
export const setAuthToken = (token) => {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

/**
 * Get authentication token from sessionStorage
 */
export const getAuthToken = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);

  return token;
};

/**
 * Store user data in sessionStorage (clears when browser closes)
 */
export const setUserData = (user) => {
  const previousUser = getUserData();

  // If account changes in the same browser session, drop stale in-progress assessment state.
  if (
    user &&
    previousUser &&
    previousUser.email &&
    user.email &&
    previousUser.email.toLowerCase() !== user.email.toLowerCase()
  ) {
    clearAssessmentSessionState();
  }

  if (user) {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(USER_KEY);
  }
};

/**
 * Get user data from sessionStorage
 */
export const getUserData = () => {
  const user = sessionStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * Get user - alias for getUserData
 */
export const getUser = () => {
  return getUserData();
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = () => {
  const hasToken = !!getAuthToken();

  return hasToken;
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  clearAssessmentSessionState();
  localStorage.removeItem('oshmLoggedIn'); // Remove legacy flag
  localStorage.removeItem('oshmUser'); // Remove legacy user data
  localStorage.removeItem('user'); // Remove legacy profile cache
  localStorage.removeItem('userEmail'); // Remove legacy checkout prefill key
};

/**
 * Validate token with backend
 * @returns {Promise<{valid: boolean, user: object|null}>}
 */
export const validateToken = async () => {
  const token = getAuthToken();
  
  if (!token) {
    return { valid: false, user: null };
  }
  
  try {
    // Get API_BASE_URL from environment or config
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    const response = await fetch(`${API_BASE_URL}/api/auth/validate-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    
    if (data.success && data.valid) {
      // Update user data if returned
      if (data.user) {
        setUserData(data.user);
      }
      return { valid: true, user: data.user };
    } else {
      // Token is invalid or expired - only clear if explicitly rejected
      if (data.requiresLogin) {
        clearAuth();
      }
      return { valid: false, user: null };
    }
  } catch (error) {
    // Network error - don't clear auth, just return invalid
    // Keep session intact on network errors
    return { valid: false, user: getUserData() };
  }
};

/**
 * Make authenticated API request
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers
  });
};
